const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;
const API_URL = 'https://api.deepseek.com/v1/chat/completions';

/**
 * Check grammar of the given text using DeepSeek AI.
 * Supports Malay (Bahasa Melayu) and English.
 * Returns an array of { word, suggestion, type, explanation } or empty array.
 */
export async function checkGrammar(text) {
  if (!text.trim()) return [];
  if (!API_KEY) {
    console.warn(
      'DeepSeek API key not found. Set REACT_APP_DEEPSEEK_API_KEY in .env.local'
    );
    return [];
  }

  const systemPrompt = `You are a strict grammar checker for Malay (Bahasa Melayu) and English.
Analyze the user's text and return a JSON array of errors found.
For each error include:
- "word": the exact incorrect text
- "suggestion": the corrected text
- "type": one of "spelling", "grammar", "preposition", "punctuation", "style"
- "explanation": brief explanation in the same language as the text

Rules:
- Only return real errors, do not over-correct
- If the text is correct, return empty array []
- Return ONLY valid JSON, no markdown, no extra text`;

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`,
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: text },
        ],
        temperature: 0.1,
        max_tokens: 2048,
      }),
    });

    if (!response.ok) {
      throw new Error(`DeepSeek API error: ${response.status}`);
    }

    const data = await response.json();
    const raw = data?.choices?.[0]?.message?.content || '[]';

    // Strip markdown code fences if present
    const match = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    const jsonStr = (match ? match[1] : raw).trim();
    return JSON.parse(jsonStr);
  } catch (error) {
    console.error('Grammar check failed:', error);
    return [];
  }
}

/**
 * Returns true if an API key has been configured.
 */
export function isGrammarCheckAvailable() {
  return !!API_KEY;
}
